// Enhanced WiFiVerification.js with improved network detection
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Check if the user is connected to an approved Wi-Fi network
 * This uses network RTT (Round Trip Time) characteristics to identify networks
 * 
 * @param {string} orgId - Organization ID
 * @returns {Promise<{isApproved: boolean, networkName: string|null, message: string}>}
 */
export const checkWiFiNetwork = async (orgId) => {
    try {
        // Get approved networks from Firestore
        const networksRef = collection(db, "organizations", orgId, "approvedNetworks");
        const networksSnapshot = await getDocs(networksRef);
        const approvedNetworks = networksSnapshot.docs.map(doc => doc.data());

        if (approvedNetworks.length === 0) {
            return {
                isApproved: false,
                networkName: null,
                message: "No approved networks configured for this organization."
            };
        }

        // Test if we're on a specific network by measuring RTT to known endpoints
        const currentNetworkInfo = await detectNetwork(approvedNetworks);

        if (currentNetworkInfo.isMatch) {
            return {
                isApproved: true,
                networkName: currentNetworkInfo.networkName,
                message: `Approved network detected: ${currentNetworkInfo.networkName}`
            };
        } else {
            return {
                isApproved: false,
                networkName: currentNetworkInfo.detectedName || null,
                message: "Not connected to an approved network."
            };
        }
    } catch (error) {
        console.error("Wi-Fi verification error:", error);
        return {
            isApproved: false,
            networkName: null,
            message: `Error during network verification: ${error.message}`
        };
    }
};

/**
 * Detect network by checking RTT characteristics with improved detection
 * 
 * @param {Array} approvedNetworks - List of approved networks with their characteristics
 * @returns {Promise<{isMatch: boolean, networkName: string|null, detectedName: string|null}>}
 */
const detectNetwork = async (approvedNetworks) => {
    // If Navigator Connection API is available, use it to get some network info
    const connection = navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

    // Get RTT if available (only in Chrome)
    let rtt = connection?.rtt || null;
    let effectiveType = connection?.effectiveType || null;
    let downlink = connection?.downlink || null;

    // If we don't have RTT (most browsers), measure it manually with multiple samples
    if (!rtt) {
        // Take multiple RTT measurements to improve accuracy
        const rttSamples = [];
        for (let i = 0; i < 3; i++) {
            const sample = await measureNetworkRTT();
            rttSamples.push(sample);
            // Small delay between samples
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Remove outliers and calculate average RTT
        rttSamples.sort((a, b) => a - b);
        // Use middle value if we have 3 samples
        if (rttSamples.length === 3) {
            rtt = rttSamples[1];
        } else {
            // Or average if we have fewer
            rtt = rttSamples.reduce((sum, val) => sum + val, 0) / rttSamples.length;
        }
    }

    // Try to get a network name hint if available
    let detectedName = null;
    try {
        // Some devices may expose a non-exact network name through the connection API
        detectedName = connection?.type || "Unknown Network";
    } catch (e) {
        console.error("Error getting network type:", e);
    }

    // Get IP address using a service (this is just an approximation)
    const ipInfo = await fetch('https://api.ipify.org?format=json')
        .then(response => response.json())
        .catch(() => ({ ip: null }));

    // Compare current network characteristics with stored approved networks
    for (const network of approvedNetworks) {
        // Use a wider RTT range to be more lenient (30% margin instead of 20%)
        const minRTT = network.minRTT || 0;
        const maxRTT = network.maxRTT || 1000;
        const rttMargin = (maxRTT - minRTT) * 0.3;

        // Check if RTT is within the expected range with a wider margin
        const rttMatch = rtt >= (minRTT - rttMargin) && rtt <= (maxRTT + rttMargin);

        // Check if the IP subnet matches (if configured)
        const ipMatch = !network.ipSubnet ||
            (ipInfo.ip && ipInfo.ip.startsWith(network.ipSubnet));

        // For corporate networks, we might also check for internal resources
        const internalResourceCheck = await checkInternalResource(network.internalResourceUrl);

        // More lenient matching: if we have a match on RTT OR IP, consider it a match
        if (rttMatch || ipMatch || internalResourceCheck) {
            return {
                isMatch: true,
                networkName: network.name,
                detectedName
            };
        }
    }

    return {
        isMatch: false,
        networkName: null,
        detectedName
    };
};

/**
 * Measure network RTT by timing a request to the server with retry logic
 * 
 * @returns {Promise<number>} - RTT in milliseconds
 */
const measureNetworkRTT = async () => {
    const start = performance.now();

    try {
        // Make a small request to measure RTT
        await fetch('/api/ping', {
            method: 'GET',
            cache: 'no-store',
            // Add a cache-busting parameter
            headers: { 'Pragma': 'no-cache' }
        });
        const end = performance.now();
        return end - start;
    } catch (error) {
        try {
            // If the ping endpoint doesn't exist, use the favicon or any other small resource
            await fetch('/favicon.ico', {
                method: 'GET',
                cache: 'no-store',
                headers: { 'Pragma': 'no-cache' }
            });
            const end = performance.now();
            return end - start;
        } catch (secondError) {
            // If both fail, return a reasonable default RTT
            console.error("RTT measurement failed:", secondError);
            return 100; // Default value
        }
    }
};

/**
 * Check if an internal resource is accessible (for corporate networks)
 * 
 * @param {string|null} url - URL to an internal resource
 * @returns {Promise<boolean>}
 */
const checkInternalResource = async (url) => {
    if (!url) return false;

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);

        const response = await fetch(url, {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-store',
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        // If we can reach this internal URL, we're likely on the corporate network
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Register a new approved Wi-Fi network for the organization with improved characteristics
 * 
 * @param {string} orgId - Organization ID
 * @param {Object} networkDetails - Network details to register
 * @returns {Promise<string>} - ID of the newly created network document
 */
export const registerApprovedNetwork = async (orgId, networkDetails) => {
    try {
        // Take multiple RTT measurements for more accurate network fingerprinting
        const rttSamples = [];
        for (let i = 0; i < 5; i++) {
            const sample = await measureNetworkRTT();
            rttSamples.push(sample);
            // Small delay between samples
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Remove outliers and calculate average RTT
        rttSamples.sort((a, b) => a - b);
        // Remove highest and lowest if we have at least 4 samples
        const filteredSamples = rttSamples.length >= 4
            ? rttSamples.slice(1, -1)
            : rttSamples;

        const avgRTT = filteredSamples.reduce((sum, val) => sum + val, 0) / filteredSamples.length;

        // Calculate standard deviation to set appropriate RTT range
        const variance = filteredSamples.reduce((sum, val) => sum + Math.pow(val - avgRTT, 2), 0) / filteredSamples.length;
        const stdDev = Math.sqrt(variance);

        // Current network characteristics
        const connection = navigator.connection ||
            navigator.mozConnection ||
            navigator.webkitConnection;

        let effectiveType = connection?.effectiveType || null;

        // Get IP address
        const ipInfo = await fetch('https://api.ipify.org?format=json')
            .then(response => response.json())
            .catch(() => ({ ip: null }));

        // Extract subnet (first two segments)
        const ipParts = ipInfo.ip ? ipInfo.ip.split('.') : [];
        const ipSubnet = ipParts.length >= 2 ? `${ipParts[0]}.${ipParts[1]}` : null;

        // Create an approved network with the current characteristics and wider margins
        const networkData = {
            name: networkDetails.name,
            // Use 2.5 standard deviations for min/max RTT to cover 99% of normal network fluctuations
            minRTT: Math.max(0, avgRTT - (2.5 * stdDev)),
            maxRTT: avgRTT + (2.5 * stdDev),
            ipSubnet: ipSubnet,
            effectiveType: effectiveType,
            avgRTT: avgRTT, // Store the average for diagnostics
            stdDevRTT: stdDev, // Store the standard deviation for diagnostics
            rttSamples: rttSamples, // Store all samples for diagnostics
            internalResourceUrl: networkDetails.internalResourceUrl || null,
            createdAt: new Date(),
            createdBy: networkDetails.userId
        };

        const docRef = await addDoc(
            collection(db, "organizations", orgId, "approvedNetworks"),
            networkData
        );

        return docRef.id;
    } catch (error) {
        console.error("Error registering network:", error);
        throw error;
    }
};